/** Unde stau pomii și boscheții pe care nu-i pune planșa: boscheți la baza
 *  pomilor, pomi și boscheți în parcurile orașului.
 *
 *  ZONE, NU PRESĂRARE UNIFORMĂ. Un câmp de densitate (`campDensitate`), neted
 *  pe zeci de metri, împarte verdele în pâlcuri și goluri: în zonele dense
 *  pomii stau aproape, cu boscheți mulți la bază și între ei; în cele rare
 *  stau pomi răsleți, fără boscheți. Un boschet nu stă niciodată singur în
 *  iarbă — lângă un pom, sau deloc: răzleț, se citea ca o pată.
 *
 *  Presărarea e DETERMINISTĂ: o grilă tremurată, cu zarul luat din coordonate,
 *  deci aceeași scenă iese la fel la fiecare deschidere. Un punct se păstrează
 *  numai dacă e pe o suprafață verde și în afara oricărui obstacol (clădire,
 *  stradă, trotuar, parcare…) — obstacolele se caută pe o grilă de celule, ca
 *  mii de clădiri ale orașului să nu se testeze toate pentru fiecare punct.
 *
 *  Fără three.js: aritmetică pe poligoane, testabilă direct. */

import type { CopacSit, TufisSit } from "./copaci3d";

type Inel = number[][];
export interface SuprafataVerde { poly: Inel; holes?: Inel[] }

function hash(i: number, j: number, s: number): number {
  let h = Math.imul(i, 0x27d4eb2d) ^ Math.imul(j, 0x165667b1) ^ Math.imul(s, 0x9e3779b1);
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  h ^= h >>> 13;
  return (h >>> 0) / 4294967296;
}

/** Un zar determinist din coordonate (la 10 cm) și o sare, în [0, 1). */
export const zar = (x: number, y: number, sare: number) => hash(Math.round(x * 10), Math.round(y * 10), sare);

export function inInel(x: number, y: number, inel: Inel): boolean {
  let in_ = false;
  for (let i = 0, j = inel.length - 1; i < inel.length; j = i++) {
    const xi = inel[i][0], yi = inel[i][1], xj = inel[j][0], yj = inel[j][1];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi || 1e-12) + xi) in_ = !in_;
  }
  return in_;
}

const peSuprafata = (x: number, y: number, s: SuprafataVerde) =>
  inInel(x, y, s.poly) && !(s.holes ?? []).some((h) => inInel(x, y, h));

function cutie(inel: Inel): [number, number, number, number] {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const p of inel) {
    if (p[0] < x0) x0 = p[0]; if (p[0] > x1) x1 = p[0];
    if (p[1] < y0) y0 = p[1]; if (p[1] > y1) y1 = p[1];
  }
  return [x0, y0, x1, y1];
}

/** Obstacolele unei scene — inele (clădiri, străzi…) și cercuri (pomi,
 *  locuri de parcare) —, puse pe celule ca să se caute numai cele din jur. */
export class Obstacole {
  private celule = new Map<string, { inele: Inel[]; cercuri: [number, number, number][] }>();
  constructor(private readonly pas = 25) {}

  private celula(i: number, j: number) {
    const k = `${i}:${j}`;
    return this.celule.get(k) ?? this.celule.set(k, { inele: [], cercuri: [] }).get(k)!;
  }

  /** Un inel ocupat, cu o margine de `margine` metri (aproximată pe cutia lui
   *  la căutare, exactă pe inel). */
  inel(inel: Inel, margine = 0): this {
    if (inel.length < 3) return this;
    const [x0, y0, x1, y1] = cutie(inel);
    const p = this.pas;
    for (let i = Math.floor((x0 - margine) / p); i <= Math.floor((x1 + margine) / p); i++) {
      for (let j = Math.floor((y0 - margine) / p); j <= Math.floor((y1 + margine) / p); j++) {
        this.celula(i, j).inele.push(margine ? Object.assign(inel.slice(), { margine }) : inel);
      }
    }
    return this;
  }

  cerc(x: number, y: number, r: number): this {
    const p = this.pas;
    for (let i = Math.floor((x - r) / p); i <= Math.floor((x + r) / p); i++) {
      for (let j = Math.floor((y - r) / p); j <= Math.floor((y + r) / p); j++) {
        this.celula(i, j).cercuri.push([x, y, r]);
      }
    }
    return this;
  }

  /** E liber un cerc de raza `r` în jurul punctului? */
  liber(x: number, y: number, r = 0): boolean {
    const c = this.celule.get(`${Math.floor(x / this.pas)}:${Math.floor(y / this.pas)}`);
    if (!c) return true;
    for (const [cx, cy, cr] of c.cercuri) {
      if ((x - cx) ** 2 + (y - cy) ** 2 < (r + cr) ** 2) return false;
    }
    for (const inel of c.inele) {
      const m = ((inel as Inel & { margine?: number }).margine ?? 0) + r;
      if (inInel(x, y, inel)) return false;
      if (m > 0 && distLaInel(x, y, inel) < m) return false;
    }
    return true;
  }
}

export function distLaInel(x: number, y: number, inel: Inel): number {
  let d = Infinity;
  for (let i = 0, j = inel.length - 1; i < inel.length; j = i++) {
    const ax = inel[j][0], ay = inel[j][1], bx = inel[i][0], by = inel[i][1];
    const dx = bx - ax, dy = by - ay;
    const L2 = dx * dx + dy * dy || 1e-12;
    const t = Math.max(0, Math.min(1, ((x - ax) * dx + (y - ay) * dy) / L2));
    d = Math.min(d, Math.hypot(x - ax - t * dx, y - ay - t * dy));
  }
  return d;
}

/** Densitatea vegetației în punct, în [0, 1): zgomot de valoare neted, pe o
 *  grilă cu latura `pas` metri. Aceeași `sare` dă aceleași pâlcuri. */
export function campDensitate(x: number, y: number, sare: number, pas = 40): number {
  const fx = x / pas, fy = y / pas;
  const i = Math.floor(fx), j = Math.floor(fy);
  const u = fx - i, v = fy - j;
  const n = (t: number) => t * t * (3 - 2 * t);
  const a = hash(i, j, sare), b = hash(i + 1, j, sare);
  const c = hash(i, j + 1, sare), d = hash(i + 1, j + 1, sare);
  return a + (b - a) * n(u) + (c - a) * n(v) + (a - b - c + d) * n(u) * n(v);
}

const neted = (a: number, b: number, t: number) => {
  const x = Math.min(1, Math.max(0, (t - a) / (b - a)));
  return x * x * (3 - 2 * x);
};

export interface Presarare {
  /** Latura grilei: un candidat pe celulă. */
  pas: number;
  /** Câți din candidații liberi se păstrează (0–1). */
  densitate: number;
  /** Înmulțește `densitate` în punct (v. `campDensitate`): pâlcuri și goluri. */
  camp?: (x: number, y: number) => number;
  /** Raza elementului, între `rMin` și `rMax`. */
  rMin: number;
  rMax: number;
  sare: number;
  /** Cel mult atâtea; când sunt mai mulți candidați, se păstrează cei mai
   *  apropiați de `centru`. */
  max?: number;
  centru?: [number, number];
}

/** Puncte presărate pe suprafețele verzi, libere de obstacole; fiecare punct
 *  păstrat devine el însuși obstacol (doi pomi nu cresc unul în altul). */
export function presara(verde: readonly SuprafataVerde[], obst: Obstacole, o: Presarare): CopacSit[] {
  const cand: (CopacSit & { d: number })[] = [];
  const [cx, cy] = o.centru ?? [0, 0];
  for (const s of verde) {
    if (s.poly.length < 3) continue;
    const [x0, y0, x1, y1] = cutie(s.poly);
    for (let i = Math.floor(x0 / o.pas); i <= Math.floor(x1 / o.pas); i++) {
      for (let j = Math.floor(y0 / o.pas); j <= Math.floor(y1 / o.pas); j++) {
        const x = (i + 0.15 + hash(i, j, o.sare + 1) * 0.7) * o.pas;
        const y = (j + 0.15 + hash(i, j, o.sare + 2) * 0.7) * o.pas;
        if (hash(i, j, o.sare) >= o.densitate * (o.camp ? o.camp(x, y) : 1)) continue;
        if (!peSuprafata(x, y, s)) continue;
        const r = o.rMin + hash(i, j, o.sare + 3) * (o.rMax - o.rMin);
        cand.push({ x, y, r, d: (x - cx) ** 2 + (y - cy) ** 2 });
      }
    }
  }
  if (o.max != null) cand.sort((a, b) => a.d - b.d);
  const out: CopacSit[] = [];
  for (const c of cand) {
    if (o.max != null && out.length >= o.max) break;
    if (!obst.liber(c.x, c.y, c.r * 0.6)) continue;
    obst.cerc(c.x, c.y, c.r * 0.6);
    out.push({ x: c.x, y: c.y, r: c.r });
  }
  return out;
}

/** Câți boscheți îi revin unui pom după densitatea zonei lui: niciunul unde
 *  pomii stau răsleți, câțiva la bază în zonele de mijloc, un tufăriș în pâlcuri. */
function boscheti(d: number, z: number): number {
  if (d < 0.38) return 0;
  if (d < 0.6) return Math.floor(z * 3);              // 0–2
  return 3 + Math.floor(z * 4);                        // 3–6
}

/** Boscheții de lângă pomi: la baza fiecăruia, câți cere densitatea zonei
 *  (`camp`; fără câmp, ca într-o zonă de mijloc), iar în pâlcuri și între doi
 *  pomi vecini — tufărișul care leagă un pâlc. Numai unde nu e un obstacol. */
export function tufisuriLaPomi(copaci: readonly CopacSit[], obst: Obstacole,
                               camp?: (x: number, y: number) => number): TufisSit[] {
  const out: TufisSit[] = [];
  const pune = (x: number, y: number, r: number) => {
    if (!obst.liber(x, y, r * 0.5)) return;
    obst.cerc(x, y, r * 0.5);
    out.push({ x, y, r });
  };
  const dens = copaci.map((c) => (camp ? camp(c.x, c.y) : 0.5));
  copaci.forEach((c, ci) => {
    const ix = Math.round(c.x * 20), iy = Math.round(c.y * 20);
    const d = dens[ci];
    const n = boscheti(d, hash(ix, iy, 71));
    const pâlc = d >= 0.6;
    for (let k = 0; k < n; k++) {
      const a = hash(ix, iy, 72 + k) * Math.PI * 2;
      const dist = c.r * (pâlc ? 0.3 + hash(ix, iy, 80 + k) * 0.9 : 0.45 + hash(ix, iy, 80 + k) * 0.45);
      const r = (pâlc ? 0.65 : 0.55) + hash(ix, iy, 90 + k) * (pâlc ? 0.6 : 0.45);
      pune(c.x + Math.cos(a) * dist, c.y + Math.sin(a) * dist, r);
    }
  });
  // între doi pomi vecini dintr-un pâlc: unul-doi boscheți pe linia dintre ei
  const pas = 12;
  const grila = new Map<string, number[]>();
  copaci.forEach((c, i) => {
    if (dens[i] < 0.6) return;
    const k = `${Math.floor(c.x / pas)}:${Math.floor(c.y / pas)}`;
    (grila.get(k) ?? grila.set(k, []).get(k)!).push(i);
  });
  copaci.forEach((a, i) => {
    if (dens[i] < 0.6) return;
    const gi = Math.floor(a.x / pas), gj = Math.floor(a.y / pas);
    for (let di = -1; di <= 1; di++) for (let dj = -1; dj <= 1; dj++) {
      for (const j of grila.get(`${gi + di}:${gj + dj}`) ?? []) {
        if (j <= i) continue;
        const b = copaci[j];
        const L = Math.hypot(b.x - a.x, b.y - a.y);
        if (L > (a.r + b.r) * 1.8) continue;
        const ix = Math.round((a.x + b.x) * 10), iy = Math.round((a.y + b.y) * 10);
        const m = 1 + Math.floor(hash(ix, iy, 101) * 2);
        for (let k = 0; k < m; k++) {
          const t = 0.3 + hash(ix, iy, 102 + k) * 0.4;
          const o = (hash(ix, iy, 110 + k) - 0.5) * 1.6;
          const nx = -(b.y - a.y) / (L || 1), ny = (b.x - a.x) / (L || 1);
          pune(a.x + (b.x - a.x) * t + nx * o, a.y + (b.y - a.y) * t + ny * o,
               0.65 + hash(ix, iy, 120 + k) * 0.5);
        }
      }
    }
  });
  return out;
}

/** Cât păstrează din candidații de pom un câmp de densitate: puțini, răsleți,
 *  unde câmpul e jos; aproape toți în pâlcuri. */
export const pastreazaPomi = (camp: (x: number, y: number) => number) =>
  (x: number, y: number) => 0.1 + 0.9 * neted(0.42, 0.72, camp(x, y));

// --------------------------------------------------------------------------- //
// PÂLCURILE LOTULUI: zone compacte, nu pomi răsleți
// --------------------------------------------------------------------------- //
//
// Pe lot, pomii nu se presară: stau în PÂLCURI — dreptunghiuri compacte de
// verde, orientate ca blocurile, mai multe prin complex, cu pomi deși și
// boscheți la bază și pe margine (Cătălin, 21.10.2026: «se vede nenatural să
// avem pomi random peste tot»). Restul peluzei rămâne iarbă. În spatele
// locurilor de parcare, un șir de boscheți ascunde mașinile.

/** Un pâlc: dreptunghiul lui (inel, în lume) și cadrul din care s-a născut. */
export interface Palc { poly: Inel; cx: number; cy: number; w: number; h: number; deg: number }

export interface Palcuire {
  /** Pasul grilei de candidați, în metri. */
  pas: number;
  /** Laturile pe care le poate avea un pâlc, în metri, de la cea mai mare la
   *  cea mai mică: pe o peluză largă iese cel mare, pe o fâșie îngustă dintre
   *  blocuri cel care încape. */
  laturi: readonly number[];
  /** Iarbă liberă între două pâlcuri, în metri. */
  gol: number;
  /** Cât se ține pâlcul departe de un obstacol (clădire, alee), în metri. */
  margine: number;
  /** Orientarea laturilor, în grade (a blocurilor; v. `orientarea`). */
  deg: number;
  /** Cât din cutia pâlcului trebuie să cadă pe verde, liber (0–1): restul
   *  cutiei nu se plantează, deci pâlcul urmează marginea terenului, drumurile
   *  și clădirile — un poligon cu oricâte laturi, nu un dreptunghi. */
  acoperire: number;
  sare: number;
}

/** Orientarea pâlcurilor: a laturii celei mai lungi a primului inel dat (un
 *  corp al lotului), adusă în [0, 90); fără inele, 0 — ca pâlcurile să stea
 *  paralel cu blocurile, nu cu nordul. */
export function orientarea(inele: readonly Inel[]): number {
  const inel = inele.find((r) => r.length >= 3);
  if (!inel) return 0;
  let best = 0, L = -1;
  for (let i = 0, j = inel.length - 1; i < inel.length; j = i++) {
    const dx = inel[i][0] - inel[j][0], dy = inel[i][1] - inel[j][1];
    const l = Math.hypot(dx, dy);
    if (l > L) { L = l; best = (Math.atan2(dy, dx) * 180) / Math.PI; }
  }
  return ((best % 90) + 90) % 90;
}

const colturile = (cx: number, cy: number, w: number, h: number, deg: number): Inel => {
  const a = (deg * Math.PI) / 180, c = Math.cos(a), s = Math.sin(a);
  return [[-w / 2, -h / 2], [w / 2, -h / 2], [w / 2, h / 2], [-w / 2, h / 2]]
    .map(([u, v]) => [cx + u * c - v * s, cy + u * s + v * c]);
};

/** Pâlcurile unei suprafețe verzi: CUTII cu laturile din `laturi`, pe grila
 *  `pas`, ținute când cel puțin `acoperire` din cutie (pe o grilă 5 × 5) cade
 *  pe verde și la `margine` de orice obstacol — ce se plantează e doar partea
 *  aia, deci pâlcul ia forma locului; fiecare candidat ia cea mai mare cutie
 *  care trece, cele mari întâi, la cel puțin `gol` una de alta. Alese în
 *  ordinea zarului, deci aceleași la fiecare deschidere. */
export function palcuri(verde: readonly SuprafataVerde[], obst: Obstacole, o: Palcuire): Palc[] {
  const cand: (Palc & { z: number })[] = [];
  const a = (o.deg * Math.PI) / 180, c = Math.cos(a), sn = Math.sin(a);
  const acoperit = (cx: number, cy: number, w: number, h: number) => {
    let n = 0;
    for (let u = -2; u <= 2; u++) {
      for (let v = -2; v <= 2; v++) {
        const px = cx + (u * w / 4) * c - (v * h / 4) * sn;
        const py = cy + (u * w / 4) * sn + (v * h / 4) * c;
        if (verde.some((s) => peSuprafata(px, py, s)) && obst.liber(px, py, o.margine)) n++;
      }
    }
    return n / 25;
  };
  const marimi = [...o.laturi].sort((p, q) => q - p);
  for (const s of verde) {
    if (s.poly.length < 3) continue;
    const [x0, y0, x1, y1] = cutie(s.poly);
    if (((x1 - x0) / o.pas) * ((y1 - y0) / o.pas) > 20000) continue;
    for (let i = Math.floor(x0 / o.pas); i <= Math.floor(x1 / o.pas); i++) {
      for (let j = Math.floor(y0 / o.pas); j <= Math.floor(y1 / o.pas); j++) {
        const cx = (i + 0.2 + hash(i, j, o.sare + 1) * 0.6) * o.pas;
        const cy = (j + 0.2 + hash(i, j, o.sare + 2) * 0.6) * o.pas;
        if (!peSuprafata(cx, cy, s) || !obst.liber(cx, cy, o.margine)) continue;
        let ales: [number, number] | null = null;
        for (const w of marimi) {
          for (const h of marimi) {
            if (ales && w * h <= ales[0] * ales[1]) continue;
            if (acoperit(cx, cy, w, h) >= o.acoperire) ales = [w, h];
          }
        }
        if (!ales) continue;
        const [w, h] = hash(i, j, o.sare + 3) < 0.5 ? ales : [ales[1], ales[0]];
        if (acoperit(cx, cy, w, h) < o.acoperire) continue;
        cand.push({ poly: colturile(cx, cy, w, h, o.deg), cx, cy, w, h, deg: o.deg, z: hash(i, j, o.sare) });
      }
    }
  }
  // cele MARI întâi: un pâlc mic ales devreme ar fi oprit unul mare în jurul
  // lui; la arie egală, zarul
  cand.sort((a, b) => (b.w * b.h - a.w * a.h) || (a.z - b.z));
  const out: Palc[] = [];
  for (const p of cand) {
    if (out.some((q) => distIntreInele(p.poly, q.poly) < o.gol)) continue;
    out.push(p);
  }
  return out;
}

/** Distanța dintre două inele convexe (0 când se ating sau se calcă): cea mai
 *  mică dintre distanțele vârfurilor unuia la laturile celuilalt. */
function distIntreInele(a: Inel, b: Inel): number {
  if (a.some((p) => inInel(p[0], p[1], b)) || b.some((p) => inInel(p[0], p[1], a))) return 0;
  let d = Infinity;
  for (const p of a) d = Math.min(d, distLaInel(p[0], p[1], b));
  for (const p of b) d = Math.min(d, distLaInel(p[0], p[1], a));
  return d;
}

/** Pomii unui pâlc: pe o grilă cu pasul `pas`, retrasă `inset` de la marginea
 *  cutiei, tremurată cu aproape un pas întreg și rărită la `densitate(pâlc)`
 *  din noduri (deasă lângă limita terenului, rară între blocuri), cu coroane
 *  de `rMin`–`rMax` — o pădurice cu goluri, nu o livadă aliniată. Numai pe
 *  verde și liber: așa pâlcul ia forma locului. Fiecare pom pus devine
 *  obstacol pentru următorii. */
export function pomiInPalcuri(palcuri: readonly Palc[], verde: readonly SuprafataVerde[], obst: Obstacole,
                              o: { pas: number; inset: number; rMin: number; rMax: number;
                                   densitate: (p: Palc) => number; sare: number }): CopacSit[] {
  const out: CopacSit[] = [];
  for (const p of palcuri) {
    const densitate = o.densitate(p);
    const a = (p.deg * Math.PI) / 180, c = Math.cos(a), sn = Math.sin(a);
    const nu = Math.max(1, Math.ceil((p.w - 2 * o.inset) / o.pas));
    const nv = Math.max(1, Math.ceil((p.h - 2 * o.inset) / o.pas));
    const du = nu > 1 ? (p.w - 2 * o.inset) / (nu - 1) : 0;
    const dv = nv > 1 ? (p.h - 2 * o.inset) / (nv - 1) : 0;
    const ix = Math.round(p.cx * 10), iy = Math.round(p.cy * 10);
    // nodul cu zarul cel mai mic rămâne oricum: un pâlc fără niciun pom n-ar fi pâlc
    let minim = 1;
    for (let i = 0; i < nu; i++) for (let j = 0; j < nv; j++) minim = Math.min(minim, hash(ix + i, iy + j, o.sare + 12));
    for (let i = 0; i < nu; i++) {
      for (let j = 0; j < nv; j++) {
        const z = hash(ix + i, iy + j, o.sare + 12);
        if (z >= densitate && z !== minim) continue;
        const u = -p.w / 2 + o.inset + i * du + (hash(ix + i, iy + j, o.sare + 5) - 0.5) * o.pas * 0.9;
        const v = -p.h / 2 + o.inset + j * dv + (hash(ix + i, iy + j, o.sare + 6) - 0.5) * o.pas * 0.9;
        const x = p.cx + u * c - v * sn, y = p.cy + u * sn + v * c;
        const r = o.rMin + hash(ix + i, iy + j, o.sare + 7) * (o.rMax - o.rMin);
        // numai partea cutiei care e pe verde și liberă: forma pâlcului
        if (!verde.some((s) => peSuprafata(x, y, s)) || !obst.liber(x, y, r * 0.45)) continue;
        obst.cerc(x, y, r * 0.45);
        out.push({ x, y, r });
      }
    }
  }
  return out;
}

/** Boscheții unui pâlc: tufe rare printre pomi, pe o grilă tremurată cu pasul
 *  `pas` — fără gard viu pe margine, pâlcul nu e o cutie desenată. Numai pe
 *  verde și unde nu stă un trunchi sau alt obstacol. */
export function boschetiInPalcuri(palcuri: readonly Palc[], verde: readonly SuprafataVerde[], obst: Obstacole,
                                  o: { pas: number; sare: number }): TufisSit[] {
  const out: TufisSit[] = [];
  for (const p of palcuri) {
    const a = (p.deg * Math.PI) / 180, c = Math.cos(a), sn = Math.sin(a);
    const ix = Math.round(p.cx * 10), iy = Math.round(p.cy * 10);
    const w = p.w - 1.0, h = p.h - 1.0;
    const nu = Math.max(1, Math.round(w / o.pas)), nv = Math.max(1, Math.round(h / o.pas));
    for (let i = 0; i < nu; i++) {
      for (let j = 0; j < nv; j++) {
        if (hash(ix + i, iy + j, o.sare + 15) >= 0.6) continue;
        const u = -w / 2 + (i + 0.5) * (w / nu) + (hash(ix + i, iy - j, o.sare + 9) - 0.5) * o.pas * 0.8;
        const v = -h / 2 + (j + 0.5) * (h / nv) + (hash(ix - i, iy + j, o.sare + 10) - 0.5) * o.pas * 0.8;
        const x = p.cx + u * c - v * sn, y = p.cy + u * sn + v * c;
        const r = 0.5 + hash(ix + i, iy + j, o.sare + 11) * 0.45;
        if (!verde.some((s) => peSuprafata(x, y, s)) || !obst.liber(x, y, r * 0.5)) continue;
        obst.cerc(x, y, r * 0.5);
        out.push({ x, y, r });
      }
    }
  }
  return out;
}

/** Locul de parcare, cum îl știe planșa: centrul, direcția laturii lungi
 *  (`deg`, local +y de-a lungul ei), lățimea `w` și lungimea `d`. */
export interface LocParcare { cx: number; cy: number; deg: number; w: number; d: number }

/** Șirul de boscheți din SPATELE locurilor de parcare: pe latura scurtă
 *  dinspre iarbă — cea mai depărtată de carosabil —, la `retras` m dincolo de
 *  loc, la `pas` m unul de altul, numai pe verde și unde e liber. Locurile
 *  vecine dintr-un rând dau împreună un gard viu continuu. */
export function tufisuriDupaParcari(locuri: readonly LocParcare[], drumuri: readonly Inel[],
                                    verde: readonly SuprafataVerde[], obst: Obstacole,
                                    o: { pas: number; retras: number; sare: number }): TufisSit[] {
  const out: TufisSit[] = [];
  if (!drumuri.length) return out;
  for (const l of locuri) {
    const a = (l.deg * Math.PI) / 180;
    const ux = -Math.sin(a), uy = Math.cos(a);          // de-a lungul laturii lungi
    const vx = Math.cos(a), vy = Math.sin(a);           // de-a latul locului
    const capete = [1, -1].map((s) => {
      const x = l.cx + ux * s * l.d / 2, y = l.cy + uy * s * l.d / 2;
      return { s, x, y, d: Math.min(...drumuri.map((d) => (inInel(x, y, d) ? 0 : distLaInel(x, y, d)))) };
    });
    const [fata, spate] = capete[0].d <= capete[1].d ? [capete[0], capete[1]] : [capete[1], capete[0]];
    if (spate.d - fata.d < 0.5) continue;                // nu se știe care e spatele
    const bx = spate.x + ux * spate.s * o.retras, by = spate.y + uy * spate.s * o.retras;
    const n = Math.max(1, Math.round(l.w / o.pas));
    const ix = Math.round(l.cx * 10), iy = Math.round(l.cy * 10);
    for (let k = 0; k < n; k++) {
      const t = (k - (n - 1) / 2) * (l.w / n);
      const x = bx + vx * t, y = by + vy * t;
      if (!verde.some((s) => peSuprafata(x, y, s))) continue;
      const r = 0.5 + hash(ix, iy + k, o.sare) * 0.25;
      if (!obst.liber(x, y, r * 0.5)) continue;
      obst.cerc(x, y, r * 0.5);
      out.push({ x, y, r });
    }
  }
  return out;
}
